
import { format, addMinutes, subMinutes } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { parseStoredDateTime } from "@/utils/timeUtils";

export const createTravelBlock = async (
  bookingId: number, 
  bookingStartTime: Date,
  bookingEndTime: Date, 
  serviceId: number, 
  userProfile: any
) => {
  try {
    console.log("Starting travel block creation for booking:", bookingId);
    
    // First check if travel time is enabled for this service
    const { data: serviceData, error: serviceError } = await supabase
      .from('services')
      .select('travel_time_enabled, travel_duration')
      .eq('id', serviceId)
      .single();
    
    if (serviceError || !serviceData || !serviceData.travel_time_enabled) {
      console.log('Travel time not enabled or service data missing - skipping travel blocks');
      return false;
    }
    
    const travelMinutes = serviceData.travel_duration === '60' ? 60 : 30;
    
    // Calculate travel block times
    const travelToStart = subMinutes(bookingStartTime, travelMinutes);
    const travelToEnd = new Date(bookingStartTime);
    const travelFromStart = new Date(bookingEndTime);
    const travelFromEnd = addMinutes(bookingEndTime, travelMinutes);
    
    // Get existing bookings in a single query to minimize database calls
    const { data: allBookings, error: bookingsError } = await supabase
      .from('bookings')
      .select('id, start_time, end_time')
      .eq('service_id', serviceId)
      .neq('id', bookingId)
      .not('status', 'in', '(rejected,cancelled)');
    
    if (bookingsError) {
      console.error('Error fetching bookings for travel block check:', bookingsError);
      return false;
    }

    // Process both travel blocks in parallel
    const travelBlockPromises = [];
    
    // Process "before" travel block
    travelBlockPromises.push(
      processTravelBlock(
        'before',
        serviceId,
        bookingId,
        travelToStart,
        travelToEnd,
        allBookings,
        userProfile
      )
    );
    
    // Process "after" travel block
    travelBlockPromises.push(
      processTravelBlock(
        'after',
        serviceId,
        bookingId,
        travelFromStart,
        travelFromEnd,
        allBookings,
        userProfile
      )
    );
    
    // Wait for both travel blocks to be processed
    const results = await Promise.allSettled(travelBlockPromises);
    console.log('Travel block creation results:', results);
    
    return results.some(result => result.status === 'fulfilled' && result.value);
  } catch (error) {
    console.error('Error in createTravelBlock:', error);
    return false;
  }
};

// Helper function to process a single travel block
const processTravelBlock = async (
  blockType: 'before' | 'after',
  serviceId: number,
  bookingId: number,
  startTime: Date,
  endTime: Date,
  existingBookings: any[],
  userProfile: any
) => {
  try {
    console.log(`Processing ${blockType} travel block for booking ${bookingId}`);
    
    // Check for adjacent bookings
    const hasAdjacentBooking = existingBookings.some(booking => {
      if (blockType === 'before') {
        const bookingEndTime = parseStoredDateTime(booking.booking_date || booking.end_time, booking.end_time_local);
        return format(bookingEndTime, 'yyyy-MM-dd HH:mm') === format(startTime, 'yyyy-MM-dd HH:mm');
      } else {
        const bookingStartTime = parseStoredDateTime(booking.booking_date || booking.start_time, booking.start_time_local);
        return format(bookingStartTime, 'yyyy-MM-dd HH:mm') === format(endTime, 'yyyy-MM-dd HH:mm');
      }
    });
    
    if (hasAdjacentBooking) {
      console.log(`Found adjacent booking for ${blockType} travel block - skipping creation`);
      return false;
    }
    
    // Check for conflicts
    const hasConflict = existingBookings.some(booking => {
      const bookingStartTime = parseStoredDateTime(booking.booking_date || booking.start_time, booking.start_time_local);
      const bookingEndTime = parseStoredDateTime(booking.booking_date || booking.end_time, booking.end_time_local);
      
      // Check if there's any overlap
      return (
        (startTime < bookingEndTime && endTime > bookingStartTime)
      );
    });
    
    if (hasConflict) {
      console.log(`Found conflict for ${blockType} travel block - skipping creation`);
      return false;
    }
    
    // Create the travel block
    const { formatDateTimeForStorage } = await import('@/utils/timeUtils');
    
    const travelBlockData = {
      service_id: serviceId,
      date: format(startTime, 'yyyy-MM-dd'),
      start_time: formatDateTimeForStorage(startTime, format(startTime, 'HH:mm')),
      end_time: formatDateTimeForStorage(endTime, format(endTime, 'HH:mm')),
      customer_id: userProfile?.id,
      customer_name: 'TRAVEL',
      status: 'travel',
      related_booking_id: bookingId
    };
    
    const { data: travelBlock, error: travelError } = await supabase
      .from('bookings')
      .insert(travelBlockData)
      .select('id');
    
    if (travelError) {
      console.error(`Error creating ${blockType} travel block:`, travelError);
      return false;
    }
    
    console.log(`Successfully created ${blockType} travel block with ID:`, travelBlock?.[0]?.id);
    return true;
  } catch (error) {
    console.error(`Error processing ${blockType} travel block:`, error);
    return false;
  }
};
